import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff, Lock, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { motion } from 'framer-motion';

// Constants
const CORRECT_PASSWORD = 'a*zZ&wY5jcR$7j';
const AUTH_STORAGE_KEY = 'auth_state';
const AUTH_EXPIRY_KEY = 'auth_expiry';
const AUTH_EXPIRY_DAYS = 30;
const MAX_ATTEMPTS = 5;
const LOCKOUT_TIME = 15 * 60 * 1000; // 15 minutes in milliseconds

interface SimplePasswordGateProps {
  children: React.ReactNode;
}

const SimplePasswordGate: React.FC<SimplePasswordGateProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [remainingAttempts, setRemainingAttempts] = useState(MAX_ATTEMPTS);
  const [isLocked, setIsLocked] = useState(false);
  const [lockoutTimeRemaining, setLockoutTimeRemaining] = useState(0);
  const [formattedLockoutTime, setFormattedLockoutTime] = useState('');

  // Check authentication state on mount
  useEffect(() => {
    const checkAuth = () => {
      try {
        // Check for lockout
        const lockedUntil = localStorage.getItem('auth_lockout');
        if (lockedUntil) {
          const lockoutTime = parseInt(lockedUntil, 10);
          if (lockoutTime > Date.now()) {
            // Still locked out
            setIsLocked(true);
            setLockoutTimeRemaining(lockoutTime - Date.now());
            startLockoutTimer(lockoutTime - Date.now());
            setIsLoading(false);
            return;
          } else {
            // Lockout expired
            localStorage.removeItem('auth_lockout');
            setRemainingAttempts(MAX_ATTEMPTS);
          }
        }

        // Check for remaining attempts
        const savedAttempts = localStorage.getItem('auth_attempts');
        if (savedAttempts) {
          setRemainingAttempts(parseInt(savedAttempts, 10));
        }

        // Check authentication state
        const authState = localStorage.getItem(AUTH_STORAGE_KEY);
        const authExpiry = localStorage.getItem(AUTH_EXPIRY_KEY);

        if (authState === 'authenticated' && authExpiry) {
          const expiryDate = new Date(authExpiry);
          if (expiryDate > new Date()) {
            setIsAuthenticated(true);
          } else {
            // Auth expired, clear it
            localStorage.removeItem(AUTH_STORAGE_KEY);
            localStorage.removeItem(AUTH_EXPIRY_KEY);
          }
        }
      } catch (error) {
        console.error('Error checking authentication:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Format the lockout time remaining
  useEffect(() => {
    if (isLocked && lockoutTimeRemaining > 0) {
      const minutes = Math.floor(lockoutTimeRemaining / 60000);
      const seconds = Math.floor((lockoutTimeRemaining % 60000) / 1000);
      setFormattedLockoutTime(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    }
  }, [isLocked, lockoutTimeRemaining]);

  // Start a timer to update the lockout time remaining
  const startLockoutTimer = (duration: number) => {
    const interval = setInterval(() => {
      setLockoutTimeRemaining((prev) => {
        const newTime = prev - 1000;
        if (newTime <= 0) {
          clearInterval(interval);
          setIsLocked(false);
          localStorage.removeItem('auth_lockout');
          setRemainingAttempts(MAX_ATTEMPTS);
          localStorage.setItem('auth_attempts', MAX_ATTEMPTS.toString());
          return 0;
        }
        return newTime;
      });
    }, 1000);

    return () => clearInterval(interval);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!password.trim() || isLocked) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Check remaining attempts
      if (remainingAttempts <= 0) {
        const lockoutTime = Date.now() + LOCKOUT_TIME;
        localStorage.setItem('auth_lockout', lockoutTime.toString());
        setIsLocked(true);
        setLockoutTimeRemaining(LOCKOUT_TIME);
        startLockoutTimer(LOCKOUT_TIME);
        setLoginError('Too many failed attempts. Account locked for 15 minutes.');
        return;
      }

      // Simple password comparison
      if (password === CORRECT_PASSWORD) {
        // Set authentication state
        setIsAuthenticated(true);
        setLoginError(null);
        setRemainingAttempts(MAX_ATTEMPTS);

        // Store authentication in localStorage with expiry
        const expiryDate = new Date();
        expiryDate.setDate(expiryDate.getDate() + AUTH_EXPIRY_DAYS);

        localStorage.setItem(AUTH_STORAGE_KEY, 'authenticated');
        localStorage.setItem(AUTH_EXPIRY_KEY, expiryDate.toISOString());
        localStorage.setItem('auth_attempts', MAX_ATTEMPTS.toString());
        localStorage.removeItem('auth_lockout');
      } else {
        // Decrement remaining attempts
        const newAttempts = remainingAttempts - 1;
        setRemainingAttempts(newAttempts);
        localStorage.setItem('auth_attempts', newAttempts.toString());

        setLoginError(`Incorrect password. ${newAttempts} attempts remaining.`);
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoginError('An error occurred during login. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0f172a]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#60a5fa] mx-auto mb-4"></div>
          <p className="text-lg text-white">Loading...</p>
        </div>
      </div>
    );
  }

  // If authenticated, render children
  if (isAuthenticated) {
    return <>{children}</>;
  }

  // Otherwise, show password gate
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0f172a] p-4">
      <Card className="w-full max-w-md bg-[#1e293b] text-white border-[#4b5563]">
        <CardHeader className="space-y-1 flex flex-col items-center">
          <div className="w-full flex justify-center mb-6">
            <img
              src="/logo.png"
              alt="ServicePane Logo"
              className="h-20 object-contain"
            />
          </div>
          <CardTitle className="text-2xl font-bold text-center text-[#60a5fa]">
            Password Protected
          </CardTitle>
          <CardDescription className="text-center text-gray-400">
            Enter the password to access the application
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-white">
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pr-10 bg-[#1e293b] text-white border-[#4b5563]"
                    disabled={isLocked || isSubmitting}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-white"
                    disabled={isLocked}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              {loginError && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Alert variant="destructive" className="bg-red-900/30 border-red-800 text-red-200">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{loginError}</AlertDescription>
                  </Alert>
                </motion.div>
              )}

              {isLocked && (
                <div className="text-center text-amber-400">
                  <p>Account locked for security reasons</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Try again in {formattedLockoutTime}
                  </p>
                </div>
              )}
            </div>
          </form>
        </CardContent>
        <CardFooter>
          <Button
            type="submit"
            className="w-full bg-[#60a5fa] hover:bg-[#3b82f6] text-white"
            onClick={handleSubmit}
            disabled={isLocked || isSubmitting || !password.trim()}
          >
            {isSubmitting ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                <span>Verifying...</span>
              </div>
            ) : (
              <div className="flex items-center">
                <Lock className="mr-2 h-4 w-4" />
                <span>Unlock Application</span>
              </div>
            )}
          </Button>
        </CardFooter>
        <div className="px-6 pb-6 pt-2 text-center">
          <p className="text-xs text-gray-400">
            This is a temporary security measure. Please contact your administrator if you need assistance.
          </p>
        </div>
      </Card>
    </div>
  );
};

export default SimplePasswordGate;
